import { useCallback, useEffect, useMemo, useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { CircleHelpIcon, XIcon } from 'lucide-react';
import { Resolver, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'sonner';
import * as yup from 'yup';

import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import { MultiSelectField } from '@/components/atoms/MultiSelect';
import {
  SidebarWrapper,
  SidebarWrapperContent,
  SidebarWrapperFooter,
  SidebarWrapperForm,
  SidebarWrapperTitle,
} from '@/components/atoms/SidebarWrapper/SidebarWrapper';
import { Button } from '@/components/ui/button';
import { URLInput } from '@/components/ui/custom/URLInput/URLInput';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { setSidebarContent } from '@/redux/sidebar/actions';
import { selectSidebar } from '@/redux/sidebar/selectors';
import { RootState } from '@/shared/types/state';
import {
  EVENT_NOTIFICATION_SECRET_DOCS_URL,
  EVENT_NOTIFICATION_URL_DOCS_URL,
} from '@/util/constants';
import { isUrl } from '@/util/urlUtils';
import { LENGTH_1024, LENGTH_XS, LENGTH_XXXL } from '@/util/validation/constants';
import { urlPattern } from '@/util/validation/patterns';

import { useEventOptions } from '../../hooks/useEventOptions';
import usePlugins from '../../hooks/usePlugins';

interface FormValues {
  name: string;
  events: string[];
  url: string;
  secret: string;
  verifySsl: boolean;
}

export const CreatePluginPanel = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [isTesting, setIsTesting] = useState(false);

  const pluginId = useSelector((state: RootState) => selectSidebar(state).meta?.pluginId);
  const { plugins, createPlugin, createStatus, updatePlugin, updateStatus } = usePlugins();
  const EVENT_OPTIONS = useEventOptions();

  const plugin = useMemo(
    () => (pluginId ? plugins.find((p) => p.plugin_id === pluginId) : undefined),
    [pluginId, plugins]
  );
  const isEditing = !!plugin;

  const schema = useMemo(
    () =>
      yup.object().shape({
        name: yup
          .string()
          .required(t('validation.required'))
          .max(LENGTH_XS, t('validation.less_than', { 0: LENGTH_XS })),
        events: yup
          .array()
          .of(yup.string().required())
          .min(1, t('validation.required'))
          .required(t('validation.required')),
        url: yup
          .string()
          .required(t('validation.required'))
          .max(LENGTH_XXXL, t('validation.less_than', { 0: LENGTH_XXXL }))
          .matches(urlPattern, t('errors.invalid_url')),
        secret: isEditing
          ? yup.string().default('')
          : yup
              .string()
              .required(t('validation.required'))
              .max(LENGTH_1024, t('validation.less_than', { 0: LENGTH_1024 })),
        verifySsl: yup.boolean().required(),
      }),
    [t, isEditing]
  );

  const form = useForm<FormValues>({
    mode: 'onChange',
    resolver: yupResolver(schema) as Resolver<FormValues>,
    defaultValues: { name: '', events: [], url: '', secret: '', verifySsl: true },
  });

  useEffect(() => {
    if (plugin) {
      form.reset({
        name: plugin.name,
        events: plugin.events,
        url: plugin.url,
        secret: plugin.token,
        verifySsl: plugin.verify_ssl,
      });
    }
  }, [plugin, form]);

  const handleClose = useCallback(() => {
    dispatch(setSidebarContent(null));
  }, [dispatch]);

  const onSubmit = useCallback(
    (data: FormValues) => {
      const pluginData = {
        name: data.name,
        events: data.events,
        url: data.url,
        token: data.secret,
        verify_ssl: data.verifySsl,
      };

      if (isEditing && pluginId) {
        const { token: _, ...updateData } = pluginData;
        updatePlugin(
          { id: pluginId, data: updateData },
          {
            onSuccess: () => {
              dispatch(setSidebarContent(null));
            },
            onError: (error) => {
              toast.error(error.message);
            },
          }
        );
      } else {
        createPlugin(
          { ...pluginData, active: true },
          {
            onSuccess: () => {
              dispatch(setSidebarContent(null));
            },
            onError: (error) => {
              toast.error(error.message);
            },
          }
        );
      }
    },
    [isEditing, pluginId, createPlugin, updatePlugin, dispatch]
  );

  const testUrl = useCallback(async () => {
    const url = form.getValues('url');
    const secret = form.getValues('secret');

    if (!url || !isUrl(url)) {
      toast.error(t('errors.invalid_url'));
      return;
    }

    if (!secret) {
      toast.error(t('plugins.secret_required'));
      return;
    }

    try {
      setIsTesting(true);
      const response = await fetch('/www/plugin-test', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url, secret }),
      });

      let result;
      try {
        const text = await response.text();
        result = text ? JSON.parse(text) : {};
      } catch {
        toast.error(`${t('plugins.test_failed')}: Invalid response format`);
        return;
      }

      if (response.ok && result.success) {
        toast.success(t('plugins.test_successful'));
      } else {
        const errorMessage =
          result.error ||
          (result.status && result.statusText
            ? `${result.status} ${result.statusText}`
            : 'Unknown error');
        toast.error(`${t('plugins.test_failed')}: ${errorMessage}`);
      }
    } catch (error) {
      toast.error(
        `${t('plugins.test_failed')}: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    } finally {
      setIsTesting(false);
    }
  }, [form, t]);

  const isPending = isEditing ? updateStatus === 'pending' : createStatus === 'pending';
  const isSubmitDisabled = (isEditing && !form.formState.isDirty) || isPending;

  return (
    <SidebarWrapper>
      <SidebarWrapperTitle className="pr-4 pl-6">
        <span className="text-base font-medium text-foreground">
          {isEditing ? t('plugins.update_plugin') : t('developer_tools.create_event')}
        </span>
        <Button variant="ghost-neutral" size="icon-sm" onClick={handleClose}>
          <XIcon size={16} />
        </Button>
      </SidebarWrapperTitle>

      <Form {...form}>
        <SidebarWrapperForm onSubmit={form.handleSubmit(onSubmit)}>
          <SidebarWrapperContent className="px-1">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('common.name')}</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      autoFocus
                      placeholder={t('developer_tools.plugin_name_placeholder')}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="events"
              render={({ field, fieldState }) => (
                <FormItem>
                  <FormLabel>{t('common.type')}</FormLabel>
                  <FormControl>
                    <MultiSelectField
                      title={t('developer_tools.plugin_type_placeholder')}
                      error={!!fieldState.error}
                      options={EVENT_OPTIONS}
                      value={field.value || []}
                      onChange={(selectedValues) => {
                        field.onChange(selectedValues);
                        form.trigger('events');
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="url"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="flex items-center gap-1">
                    {t('plugins.url')}
                    <InfoTooltip
                      content={t('plugins.url_tooltip')}
                      learnMoreUrl={EVENT_NOTIFICATION_URL_DOCS_URL}
                      side="right"
                      size="large"
                    >
                      <CircleHelpIcon size={12} className="text-foreground-muted" />
                    </InfoTooltip>
                  </FormLabel>
                  <FormControl>
                    <URLInput
                      value={field.value ?? ''}
                      onChange={field.onChange}
                      className="w-full"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="secret"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="flex items-center gap-1">
                    {t('plugins.secret')}
                    <InfoTooltip
                      content={t('plugins.secret_tooltip')}
                      learnMoreUrl={EVENT_NOTIFICATION_SECRET_DOCS_URL}
                      side="right"
                      size="large"
                    >
                      <CircleHelpIcon size={12} className="text-foreground-muted" />
                    </InfoTooltip>
                  </FormLabel>
                  <FormControl>
                    <Input {...field} type="password" disabled={isEditing} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="verifySsl"
              render={({ field: { value, onChange, ...field } }) => (
                <FormItem>
                  <Switch
                    variant="choice-card"
                    switchPosition="right"
                    label={t('plugins.verify_ssl')}
                    description={t('plugins.verify_ssl_tooltip')}
                    checked={value}
                    onCheckedChange={onChange}
                    {...field}
                  />
                </FormItem>
              )}
            />
          </SidebarWrapperContent>

          <SidebarWrapperFooter>
            <Button type="submit" disabled={isSubmitDisabled} isLoading={isPending}>
              {isEditing ? t('common.save') : t('common.create')}
            </Button>
            <Button type="button" variant="outline-neutral" onClick={testUrl} isLoading={isTesting}>
              {t('common.test')}
            </Button>
          </SidebarWrapperFooter>
        </SidebarWrapperForm>
      </Form>
    </SidebarWrapper>
  );
};
